import type { SxProps, Theme } from "@mui/material";

import { Box, Divider, Icon, IconButton, List, ListItemButton, ListItemIcon, ThemeProvider } from "@mui/material";
import { Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { canViewOwnCommissions } from "~/auth/roleChecks";
import useMyUser from "~/auth/useMyUser";
import ModalDialog from "~/components/ModalDialog";
import { BodyText, CaptionText, SubtitleText } from "~/components/Typographies";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import buildPreferenceTabs, { type PreferenceSection } from "~/layout/preferencesModalTabs";
import AppThemeContext from "~/providers/AppThemeContext";
import useInstallAppGate from "~/pwa/useInstallAppGate";
import { closePreferencesModal } from "~/store/layoutSlice";
import { themeCreator } from "~/themes/pageThemes";

const VERTICAL_RAIL_MIN_WIDTH = 640;
const RAIL_WIDTH = 232;

const paperSx: SxProps<Theme> = {
	height: "min(85vh, 720px)",
	overflow: "hidden",
	"& .MuiDialogContent-root": {
		display: "flex",
		flexDirection: "column",
		minHeight: 0,
		overflow: "hidden",
		p: 0
	}
};

const railItemSx: SxProps<Theme> = {
	borderRadius: 2,
	color: "text.secondary",
	mb: 0.5,
	px: 1.5,
	py: 1,
	"& .MuiListItemIcon-root": { color: "inherit", minWidth: 36 },
	"&.Mui-selected, &.Mui-selected:hover": {
		bgcolor: "primary.main",
		color: "primary.contrastText"
	}
};

const PreferencesModal = () => {
	const { t } = useTranslation("common");
	const dispatch = useAppDispatch();
	const open = useAppSelector((state) => state.layout.preferencesModalOpen);
	const requestedTab = useAppSelector((state) => state.layout.preferencesModalTab);
	const { canShowInstall } = useInstallAppGate();
	const { myUser } = useMyUser();
	const showMyCommissions = canViewOwnCommissions(myUser?.roles);
	const { themeSize, themeType } = useContext(AppThemeContext);
	const worksuiteTheme = useMemo(() => themeCreator("worksuite", themeType, themeSize), [themeType, themeSize]);
	const [tabId, setTabId] = useState("preferences");
	const [verticalRail, setVerticalRail] = useState(true);
	const [drilledIn, setDrilledIn] = useState(false);
	const teardownRef = useRef<(() => void) | null>(null);

	const tabs = useMemo(
		() => buildPreferenceTabs({ canShowInstall, showMyCommissions }),
		[canShowInstall, showMyCommissions]
	);
	const sections = useMemo(
		() =>
			(["preferences", "myPages"] as PreferenceSection[])
				.map((section) => ({ entries: tabs.filter((entry) => entry.section === section), section }))
				.filter(({ entries }) => entries.length > 0),
		[tabs]
	);

	useEffect(() => {
		if (!open) return;
		setTabId(requestedTab);
		setDrilledIn(requestedTab !== "preferences");
	}, [open, requestedTab]);

	const measureRail = useCallback((node: HTMLDivElement | null) => {
		teardownRef.current?.();
		teardownRef.current = null;
		if (!node) return;
		const apply = () => setVerticalRail(node.clientWidth >= VERTICAL_RAIL_MIN_WIDTH);
		apply();
		const observer = new ResizeObserver(apply);
		observer.observe(node);
		window.addEventListener("resize", apply);
		teardownRef.current = () => {
			observer.disconnect();
			window.removeEventListener("resize", apply);
		};
	}, []);

	const close = () => dispatch(closePreferencesModal());
	const hasMyPages = sections.some(({ section }) => section === "myPages");
	const active = tabs.find((entry) => entry.id === tabId) ?? tabs[0];
	const ActiveTab = active.Component;

	const selectTab = (id: string) => {
		setTabId(id);
		if (!verticalRail) setDrilledIn(true);
	};

	const showRail = verticalRail || !drilledIn;
	const showContent = verticalRail || drilledIn;

	return (
		<ThemeProvider theme={worksuiteTheme}>
			<ModalDialog
				actions={[{ children: t("actions.close"), props: { onClick: close, variant: "contained" } }]}
				close={close}
				dialogMaxWidth="1200px"
				mergeSlotProps
				open={open}
				showCloseButton
				slotProps={{ paper: { sx: paperSx } }}
				title={t(hasMyPages ? "preferences.titleWithMyPages" : "preferences.title")}
			>
				<Box ref={measureRail} sx={{ display: "flex", flex: 1, minHeight: 0 }}>
					{showRail && (
						<Box
							sx={{
								borderColor: "divider",
								borderRight: verticalRail ? 1 : 0,
								flexShrink: 0,
								overflowY: "auto",
								p: 1.5,
								width: verticalRail ? RAIL_WIDTH : "100%"
							}}
						>
							{sections.map(({ entries, section }, sectionIndex) => (
								<Box key={section}>
									{sectionIndex > 0 && <Divider sx={{ mb: 1.5, mt: 1 }} />}
									<CaptionText
										sx={{
											color: "text.secondary",
											display: "block",
											fontWeight: 600,
											letterSpacing: "0.06em",
											mb: 0.75,
											px: 1.5,
											textTransform: "uppercase"
										}}
									>
										{t(`preferences.sections.${section}`)}
									</CaptionText>
									<List disablePadding>
										{entries.map((entry) => {
											const selected = verticalRail && entry.id === active.id;
											return (
												<ListItemButton
													key={entry.id}
													onClick={() => selectTab(entry.id)}
													selected={selected}
													sx={railItemSx}
												>
													<ListItemIcon>
														<Icon>{entry.icon}</Icon>
													</ListItemIcon>
													<BodyText sx={{ fontWeight: selected ? 600 : 500 }}>{t(entry.labelKey)}</BodyText>
													{!verticalRail && <Icon sx={{ color: "action.active", ml: "auto" }}>chevron_right</Icon>}
												</ListItemButton>
											);
										})}
									</List>
								</Box>
							))}
						</Box>
					)}
					{showContent && (
						<Box sx={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 0, minWidth: 0 }}>
							{!verticalRail && (
								<Box
									sx={{
										alignItems: "center",
										borderBottom: 1,
										borderColor: "divider",
										display: "flex",
										flexShrink: 0,
										gap: 0.5,
										px: 1,
										py: 1
									}}
								>
									<IconButton aria-label={t("preferences.backToCategories")} onClick={() => setDrilledIn(false)}>
										<Icon>arrow_back</Icon>
									</IconButton>
									<SubtitleText bold>{t(active.labelKey)}</SubtitleText>
								</Box>
							)}
							<Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflowY: "auto" }}>
								<Suspense fallback={null}>
									<ActiveTab hosted />
								</Suspense>
							</Box>
						</Box>
					)}
				</Box>
			</ModalDialog>
		</ThemeProvider>
	);
};

export default PreferencesModal;
